/**
 * English translations
 */
export const en = {
  // Toolbar - Main buttons
  toolbar: {
    save: 'Save',
    load: 'Load',
    export: 'PDF Export',
    select: 'Select',
    rect: 'Rect',
    circle: 'Circle',
    arrow: 'Arrow',
    code: 'Code',
    markdown: 'Markdown',
    image: 'Image',
  },

  // Toolbar - Tooltips
  tooltips: {
    save: 'Save',
    load: 'Load',
    export: 'Export (Ctrl+Shift+E)',
    select: 'Lasso Select (V)',
    rect: 'Rectangle (R)',
    circle: 'Circle (C)',
    arrow: 'Arrow (A)',
    code: 'Code Block',
    markdown: 'Markdown',
    imageConcat: 'Concat Images',
  },

  // Toolbar - Properties
  properties: {
    startPoint: 'Start',
    endPoint: 'End',
    curved: 'Curved',
    color: 'Color',
    thickness: 'Width',
    background: 'Fill',
    border: 'Stroke',
    font: 'Font',
    priority: 'Order',
    items: 'items',
  },

  // Minimap
  minimap: {
    zoom: 'zoom',
  },

  // Collaboration
  collaboration: {
    collab: 'Collab',
    title: 'Collaboration',
    createSession: 'Create Session',
    joinSession: 'Join Session',
    leaveSession: 'Leave Session',
    sessionId: 'Session ID',
    join: 'Join',
    cancel: 'Cancel',
    users: 'Users',
    you: 'You',
    host: 'Host',
    exportUsers: 'Export Users',
    back: 'Back',
    create: 'Create',
    leave: 'Leave',
    copy: 'Copy',
    copied: 'Copied!',
    kick: 'Kick',
    participants: 'Participants',
    yourName: 'Your Name',
    enterName: 'Enter your name',
    enterSessionId: 'Enter session ID (e.g., ABC12345)',
    startNewSession: 'Start a new collaboration session or join an existing one.',
    startSession: 'Start a new collaboration session',
    joinWithId: 'Join with a session ID',
    copySessionId: 'Copy Session ID',
    sessionInfo: 'Session: {id} ({count} participants) - click to manage',
    peopleCount: '{count}',
    kickConfirm: 'Kick out {name}?',
    moreUsers: '{count} more users',
    pleaseEnterName: 'Please enter your name',
    pleaseEnterSessionId: 'Please enter session ID',
  },

  // Modals
  modals: {
    // Export Modal
    export: {
      title: 'Export',
      scope: 'Export Scope',
      allObjects: 'All Objects',
      selectedOnly: 'Selected Only',
      format: 'Format',
      quality: 'Quality',
      exportBtn: 'Export',
      exporting: 'Exporting...',
      allCanvas: 'All Canvas',
      includeAll: 'Include all objects',
      currentView: 'Current Viewport',
      visibleOnly: 'Visible area only',
      selectedObjects: 'Selected Objects',
      selectionOnly: 'Selection only',
      selectObjects: 'Select objects',
      filename: 'Filename',
      enterFilename: 'Enter filename',
      paperSize: 'Paper Size',
      fitToContent: 'Fit to content',
      orientation: 'Orientation',
      landscape: 'Landscape',
      portrait: 'Portrait',
      margin: 'Margin',
      resolution: 'Resolution',
      backgroundColor: 'Background Color',
      exportComplete: 'Export complete!',
      qualityLow: 'Low (60%)',
      qualityMedium: 'Medium (80%)',
      qualityHigh: 'High (100%)',
      exportPdf: 'Export PDF',
      exportJpg: 'Export JPG',
    },
    // Image Concat Modal
    imageConcat: {
      title: 'Concat Images',
      dragDropText: 'Drag & drop images here or click to select',
      supportedFormats: 'Supports PNG, JPG, GIF, WebP (Multiple files)',
      targetWidth: 'Target Width',
      auto: 'Auto (Match widest)',
      trimMargins: 'Trim vertical margins',
      trimDescription: '(White/transparent areas)',
      noImages: 'No images selected',
      confirm: 'Concat Images',
      cancel: 'Cancel',
      close: 'Close (ESC)',
      selectedImages: 'Selected Images',
      clearAll: 'Clear All',
      moveUp: 'Move Up',
      moveDown: 'Move Down',
      delete: 'Delete',
      resultWidth: 'Result Image Width',
      preview: 'Preview',
      processing: 'Processing...',
    },
    // Code Editor
    codeEditor: {
      title: 'Code Editor',
      language: 'Language',
      save: 'Save (Ctrl+S)',
      cancel: 'Cancel (ESC)',
      placeholder: 'Enter code...',
      hint: 'Ctrl+Enter to save, ESC to cancel',
    },
    // Markdown Editor
    markdownEditor: {
      title: 'Markdown Editor',
      preview: 'Preview',
      save: 'Save (Ctrl+S)',
      cancel: 'Cancel (ESC)',
      edit: 'Edit',
      split: 'Split',
      bold: 'Bold (Ctrl+B)',
      italic: 'Italic (Ctrl+I)',
      code: 'Code',
      placeholder: 'Enter markdown...',
      previewHint: '*Enter markdown to see preview.*',
      notionStyle: 'Notion-style Editor',
      hint: '/ slash menu • Ctrl+Enter to save • ESC to cancel',
    },
  },

  // Context Menu
  contextMenu: {
    delete: 'Delete',
    duplicate: 'Duplicate',
    bringToFront: 'Bring to Front',
    sendToBack: 'Send to Back',
  },

  // Annotation Toolbar
  annotation: {
    highlighter: 'Highlighter (H)',
    underline: 'Underline (U)',
    eraser: 'Eraser (E)',
    size: 'Size:',
    opacity: 'Opacity:',
    solid: 'Solid',
    dashed: 'Dashed',
    wavy: 'Wavy',
    clearAll: 'Clear All',
    close: 'Close (Esc)',
    deleteConfirm: 'Delete all annotations?',
  },

  // PDF Viewer
  pdf: {
    pageList: 'Page list',
    extractPage: 'Extract this page',
    extract: 'Extract',
    outline: 'Contents',
    pages: 'Pages',
    noOutline: 'No table of contents',
  },

  // Common
  common: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    close: 'Close',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    back: 'Back',
  },

  // Placeholder
  placeholder: {
    line1: 'Drag & drop PDF or image files',
    line2: 'Create shapes and add notes freely',
    line3: 'Paste images from clipboard with Ctrl+V',
  },

  // Help
  help: {
    title: 'Quick Guide',
    sections: [
      { title: 'Add PDF / Image', items: ['Drag & drop PDF or image files onto the canvas', 'Paste images from clipboard with Ctrl+V'] },
      { title: 'Annotation (Highlighter/Underline)', items: ['Double-click an image or PDF to enter annotation mode', 'Select a tool and drag to draw highlights/underlines', 'Press ESC or click outside to exit annotation mode', 'Shortcuts: H (Highlighter), U (Underline), E (Eraser)'] },
      { title: 'Object Controls', items: ['Click to select, drag to move', 'Ctrl+C / Ctrl+V: Copy / Paste', 'Ctrl+D: Duplicate in place', 'Delete: Remove', 'Ctrl+Z / Ctrl+Shift+Z: Undo / Redo'] },
      { title: 'PDF Pages', items: ['Use ◀ ▶ buttons at bottom to navigate pages', '☰ button to open page browser', '"Extract" button to extract current page as a separate object'] },
      { title: 'Canvas', items: ['Mouse wheel to zoom in/out', 'Drag empty space to pan the canvas', 'Paste a YouTube URL to add a video'] },
    ],
  },

  // Settings
  settings: {
    language: 'Language',
    english: 'English',
    korean: '한국어',
  },
};

export type TranslationKeys = typeof en;
